import React, { useEffect, useState } from 'react';
import { useStock } from '../../context/StockContext';
import toast from 'react-hot-toast';

const B = '#0B3D6E', T = '#0F6E56';
const CATS = ['medicament','dispositif','consommable','laboratoire','autre'];
const CAT_LABELS = { medicament:'💊 Médicament', dispositif:'🩺 Dispositif', consommable:'🧤 Consommable', laboratoire:'🔬 Laboratoire', autre:'📋 Autre' };
const UNITS = ['boîte','comprimé','flacon','sachet','ampoule','seringue','unité','kg','litre','ml'];
const INIT = { name:'', barcode:'', category:'medicament', description:'', unit:'boîte', costPrice:0, sellPrice:0, quantity:0, minQuantity:5, expiryDate:'', supplier:'', imageUrl:'' };

// Image upload component — supports URL or base64 preview
function ImageUpload({ value, onChange }) {
  const [tab, setTab] = useState('url');
  const [dragging, setDragging] = useState(false);

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Fichier image requis'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image trop grande (max 5 Mo)'); return; }
    const reader = new FileReader();
    reader.onload = e => onChange(e.target.result);
    reader.readAsDataURL(file);
  };

  return (
    <div>
      <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:6 }}>Image du produit</label>
      <div style={{ display:'flex', border:'1px solid #e2e8f0', borderRadius:8, overflow:'hidden', marginBottom:8 }}>
        {[['url','🔗 URL'],['upload','📁 Fichier']].map(([t,l]) => (
          <button key={t} type="button" onClick={()=>setTab(t)} style={{ flex:1, background:tab===t?B:'#fff', color:tab===t?'#fff':'#64748b', border:'none', padding:'7px', fontSize:12, fontWeight:tab===t?600:400, cursor:'pointer' }}>{l}</button>
        ))}
      </div>

      {tab === 'url' ? (
        <input type="url" placeholder="https://images.unsplash.com/..."
          value={value.startsWith('data:') ? '' : value}
          onChange={e => onChange(e.target.value)}
          style={{ width:'100%', border:'1px solid #e2e8f0', borderRadius:8, padding:'9px 12px', fontSize:13, background:'#f8fafc', outline:'none', fontFamily:'inherit', boxSizing:'border-box' }}/>
      ) : (
        <div
          onDragOver={e=>{ e.preventDefault(); setDragging(true); }}
          onDragLeave={()=>setDragging(false)}
          onDrop={e=>{ e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
          style={{ border:`2px dashed ${dragging?B:'#e2e8f0'}`, borderRadius:8, padding:'1.5rem', textAlign:'center', cursor:'pointer', background:dragging?'#E6F1FB':'#f8fafc', transition:'all 0.2s' }}
          onClick={()=>document.getElementById('img-input').click()}>
          <input id="img-input" type="file" accept="image/*" style={{ display:'none' }} onChange={e=>handleFile(e.target.files[0])}/>
          <div style={{ fontSize:28, marginBottom:8 }}>📸</div>
          <div style={{ fontSize:13, color:'#64748b' }}>Glisser-déposer ou <span style={{ color:B, fontWeight:600 }}>parcourir</span></div>
          <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>PNG, JPG, WEBP — max 5 Mo</div>
        </div>
      )}

      {value && (
        <div style={{ marginTop:8, position:'relative', display:'inline-block' }}>
          <img src={value} alt="preview" style={{ width:80, height:60, objectFit:'cover', borderRadius:8, border:'1px solid #e2e8f0', display:'block' }} onError={e=>e.target.style.display='none'}/>
          <button type="button" onClick={()=>onChange('')} style={{ position:'absolute', top:-6, right:-6, background:'#dc2626', color:'#fff', border:'none', width:18, height:18, borderRadius:'50%', fontSize:11, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
        </div>
      )}
    </div>
  );
}

export default function StockProducts() {
  const { products, fetchAll, addProduct, updateProduct, deleteProduct, addMovement } = useStock();
  const [search, setSearch]   = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(INIT);
  const [mvModal, setMvModal]   = useState(null);
  const [mv, setMv]             = useState({ type:'in', quantity:1, reason:'', note:'' });
  const [saving, setSaving]     = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const filtered = products.filter(p => {
    if (catFilter && p.category !== catFilter) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase()) && !p.barcode?.includes(search)) return false;
    return true;
  });

  const openAdd  = () => { setEditing(null); setForm(INIT); setShowModal(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name:p.name, barcode:p.barcode||'', category:p.category, description:p.description||'', unit:p.unit, costPrice:p.costPrice, sellPrice:p.sellPrice, quantity:p.quantity, minQuantity:p.minQuantity, expiryDate:p.expiryDate?p.expiryDate.slice(0,10):'', supplier:p.supplier||'', imageUrl:p.imageUrl||'' });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nom du produit requis'); return; }
    setSaving(true);
    try {
      if (editing) { await updateProduct(editing._id, form); toast.success('Produit mis à jour ✅'); }
      else {
        const res = await addProduct(form);
        if (!res.success) throw new Error(res.message);
        toast.success('Produit ajouté ✅');
      }
      setShowModal(false);
    } catch(err) {
      if (err.response?.data?.limitReached) toast.error('⚠️ Limite plan gratuit ! Passez au plan Pro.');
      else toast.error(err.response?.data?.message || err.message);
    } finally { setSaving(false); }
  };

  const handleMovement = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await addMovement({ productId:mvModal._id, ...mv, quantity:Number(mv.quantity) });
      toast.success('Mouvement enregistré ✅');
      setMvModal(null);
      setMv({ type:'in', quantity:1, reason:'', note:'' });
    } catch(err) { toast.error(err.response?.data?.message||'Erreur'); }
    finally { setSaving(false); }
  };

  const inp = { width:'100%', border:'1px solid #e2e8f0', borderRadius:8, padding:'9px 12px', fontSize:13, background:'#f8fafc', outline:'none', fontFamily:'inherit', boxSizing:'border-box' };

  const stockColor = (p) => p.quantity===0?'#dc2626':p.isLowStock?'#d97706':T;

  const margin = (p) => p.costPrice > 0 ? Math.round(((p.sellPrice - p.costPrice) / p.costPrice) * 100) : 0;

  return (
    <div style={{ maxWidth:1000, margin:'0 auto', padding:'1.5rem' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem', flexWrap:'wrap', gap:10 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:600, marginBottom:2 }}>Catalogue de stock</h1>
          <div style={{ fontSize:13, color:'#64748b' }}>{filtered.length} produit(s)</div>
        </div>
        <button onClick={openAdd} style={{ background:B, color:'#fff', border:'none', padding:'9px 18px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:7 }}>
          <span style={{ fontSize:16 }}>+</span> Ajouter produit
        </button>
      </div>

      {/* Filters + view toggle */}
      <div style={{ display:'flex', gap:10, marginBottom:'1rem', flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ position:'relative', flex:1, minWidth:180 }}>
          <svg viewBox="0 0 24 24" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', width:14, height:14, fill:'none', stroke:'#94a3b8', strokeWidth:2 }}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input type="text" placeholder="Rechercher nom, code-barres..." value={search} onChange={e=>setSearch(e.target.value)}
            style={{ ...inp, paddingLeft:32 }}/>
        </div>
        <select value={catFilter} onChange={e=>setCatFilter(e.target.value)} style={{ ...inp, width:'auto' }}>
          <option value="">Toutes catégories</option>
          {CATS.map(c=><option key={c} value={c}>{CAT_LABELS[c]}</option>)}
        </select>
        <div style={{ display:'flex', border:'1px solid #e2e8f0', borderRadius:8, overflow:'hidden' }}>
          {[['grid','⊞'],['list','≡']].map(([v,i])=>(
            <button key={v} onClick={()=>setViewMode(v)} style={{ background:viewMode===v?B:'#fff', color:viewMode===v?'#fff':'#64748b', border:'none', padding:'8px 12px', cursor:'pointer', fontSize:14 }}>{i}</button>
          ))}
        </div>
      </div>

      {/* Products */}
      {filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'4rem', color:'#94a3b8', background:'#fff', borderRadius:12, border:'1px solid #e2e8f0' }}>
          <div style={{ fontSize:48, marginBottom:12 }}>📦</div>
          <div style={{ fontSize:15, fontWeight:500 }}>Aucun produit trouvé</div>
          <button onClick={openAdd} style={{ marginTop:14, background:B, color:'#fff', border:'none', padding:'10px 20px', borderRadius:8, fontSize:13, cursor:'pointer' }}>+ Ajouter le premier produit</button>
        </div>
      ) : viewMode === 'grid' ? (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(210px,1fr))', gap:14 }}>
          {filtered.map(p => (
            <div key={p._id} style={{ background:'#fff', border:`1px solid ${p.quantity===0?'#fca5a5':p.isLowStock?'#fcd34d':'#e2e8f0'}`, borderRadius:12, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', transition:'border-color 0.15s' }}>
              <div style={{ height:110, background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', position:'relative' }}>
                {p.imageUrl
                  ? <img src={p.imageUrl} alt={p.name} style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='flex';}}/>
                  : null}
                <div style={{ width:'100%', height:'100%', display:p.imageUrl?'none':'flex', alignItems:'center', justifyContent:'center', fontSize:36 }}>💊</div>
                <span style={{ position:'absolute', top:6, left:6, background:'#fff', border:'1px solid #e2e8f0', fontSize:9, padding:'2px 7px', borderRadius:8, fontWeight:500, color:'#64748b' }}>
                  {CAT_LABELS[p.category]?.split(' ')[0]} {p.category}
                </span>
                {p.isExpiringSoon && <span style={{ position:'absolute', top:6, right:6, background:'#fef3c7', border:'1px solid #fcd34d', fontSize:9, padding:'2px 7px', borderRadius:8, fontWeight:600, color:'#d97706' }}>⚠️ Expire</span>}
              </div>
              <div style={{ padding:'10px 12px' }}>
                <div style={{ fontSize:13, fontWeight:600, marginBottom:2, lineHeight:1.3 }}>{p.name}</div>
                {p.supplier && <div style={{ fontSize:10, color:'#94a3b8', marginBottom:6 }}>📦 {p.supplier}</div>}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                  <div style={{ fontSize:11, color:'#64748b' }}>Stock actuel</div>
                  <span style={{ fontSize:14, fontWeight:700, color:stockColor(p) }}>{p.quantity} {p.unit}</span>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:4, marginBottom:8 }}>
                  <div style={{ background:'#f8fafc', borderRadius:6, padding:'4px 6px', textAlign:'center' }}>
                    <div style={{ fontSize:9, color:'#94a3b8' }}>Achat</div>
                    <div style={{ fontSize:11, fontWeight:600 }}>{p.costPrice} MAD</div>
                  </div>
                  <div style={{ background:'#f8fafc', borderRadius:6, padding:'4px 6px', textAlign:'center' }}>
                    <div style={{ fontSize:9, color:'#94a3b8' }}>Vente</div>
                    <div style={{ fontSize:11, fontWeight:600, color:T }}>{p.sellPrice} MAD</div>
                  </div>
                  <div style={{ background: margin(p)>0?'#E1F5EE':'#f8fafc', borderRadius:6, padding:'4px 6px', textAlign:'center' }}>
                    <div style={{ fontSize:9, color:'#94a3b8' }}>Marge</div>
                    <div style={{ fontSize:11, fontWeight:600, color:margin(p)>0?T:'#94a3b8' }}>{margin(p)}%</div>
                  </div>
                </div>
                <div style={{ display:'flex', gap:5 }}>
                  <button onClick={()=>setMvModal(p)} style={{ flex:2, background:T, color:'#fff', border:'none', padding:'6px', borderRadius:7, fontSize:11, fontWeight:600, cursor:'pointer' }}>± Mouvement</button>
                  <button onClick={()=>openEdit(p)} style={{ flex:1, background:'#f1f5f9', border:'1px solid #e2e8f0', padding:'6px', borderRadius:7, fontSize:11, cursor:'pointer' }}>✏️</button>
                  <button onClick={()=>{ if(window.confirm(`Supprimer "${p.name}" ?`)) deleteProduct(p._id).then(()=>toast.success('Supprimé')); }} style={{ flex:1, background:'#fee2e2', border:'1px solid #fca5a5', padding:'6px', borderRadius:7, fontSize:11, cursor:'pointer', color:'#dc2626' }}>🗑</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, overflow:'hidden' }}>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:600 }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  {['Produit','Catégorie','Stock','Achat','Vente','Marge','Actions'].map(h=>(
                    <th key={h} style={{ textAlign:'left', fontSize:11, fontWeight:600, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.5px', padding:'10px 14px', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p=>(
                  <tr key={p._id} style={{ borderTop:'1px solid #f1f5f9' }}
                    onMouseEnter={e=>e.currentTarget.style.background='#fafafa'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ padding:'10px 14px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        {p.imageUrl ? <img src={p.imageUrl} alt="" style={{ width:36, height:28, objectFit:'cover', borderRadius:6, border:'1px solid #e2e8f0' }} onError={e=>e.target.style.display='none'}/> : <div style={{ width:36, height:28, background:'#f1f5f9', borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>💊</div>}
                        <div>
                          <div style={{ fontSize:13, fontWeight:600 }}>{p.name}</div>
                          {p.supplier && <div style={{ fontSize:11, color:'#94a3b8' }}>{p.supplier}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:'10px 14px' }}><span style={{ background:'#f1f5f9', padding:'3px 8px', borderRadius:6, fontSize:11 }}>{CAT_LABELS[p.category]}</span></td>
                    <td style={{ padding:'10px 14px', fontWeight:700, color:stockColor(p), fontSize:13 }}>{p.quantity} {p.unit}</td>
                    <td style={{ padding:'10px 14px', fontSize:13 }}>{p.costPrice} MAD</td>
                    <td style={{ padding:'10px 14px', fontSize:13, color:T, fontWeight:500 }}>{p.sellPrice} MAD</td>
                    <td style={{ padding:'10px 14px', fontSize:13, color:margin(p)>0?T:'#94a3b8', fontWeight:600 }}>{margin(p)}%</td>
                    <td style={{ padding:'10px 14px' }}>
                      <div style={{ display:'flex', gap:5 }}>
                        <button onClick={()=>setMvModal(p)} style={{ fontSize:11, background:T, color:'#fff', border:'none', padding:'5px 8px', borderRadius:6, cursor:'pointer', whiteSpace:'nowrap' }}>± Mvt</button>
                        <button onClick={()=>openEdit(p)} style={{ fontSize:11, background:'#f1f5f9', border:'1px solid #e2e8f0', padding:'5px 8px', borderRadius:6, cursor:'pointer' }}>✏️</button>
                        <button onClick={()=>{ if(window.confirm(`Supprimer ?`)) deleteProduct(p._id).then(()=>toast.success('Supprimé')); }} style={{ fontSize:11, background:'#fee2e2', border:'1px solid #fca5a5', padding:'5px 8px', borderRadius:6, cursor:'pointer', color:'#dc2626' }}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}
          onClick={e=>{ if(e.target===e.currentTarget) setShowModal(false); }}>
          <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:540, maxHeight:'92vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding:'1.2rem 1.5rem', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <h2 style={{ fontSize:16, fontWeight:600 }}>{editing?'✏️ Modifier le produit':'+ Ajouter un produit'}</h2>
              <button onClick={()=>setShowModal(false)} style={{ background:'#f1f5f9', border:'none', width:28, height:28, borderRadius:7, cursor:'pointer', fontSize:16, color:'#64748b' }}>×</button>
            </div>
            <form onSubmit={handleSave} style={{ overflowY:'auto', flex:1, padding:'1.5rem', display:'flex', flexDirection:'column', gap:14 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }} className="pf">
                <style>{`@media(max-width:480px){.pf{grid-template-columns:1fr !important}}`}</style>
                <div style={{ gridColumn:'1/-1' }}>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Nom du produit *</label>
                  <input style={inp} value={form.name} onChange={e=>setForm({...form,name:e.target.value})} required placeholder="Ex: Paracétamol 500mg"/>
                </div>
                <div>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Catégorie</label>
                  <select style={inp} value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
                    {CATS.map(c=><option key={c} value={c}>{CAT_LABELS[c]}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Unité</label>
                  <select style={inp} value={form.unit} onChange={e=>setForm({...form,unit:e.target.value})}>
                    {UNITS.map(u=><option key={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Quantité initiale</label>
                  <input type="number" style={inp} min="0" value={form.quantity} onChange={e=>setForm({...form,quantity:e.target.value})}/>
                </div>
                <div>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Seuil alerte (min)</label>
                  <input type="number" style={inp} min="0" value={form.minQuantity} onChange={e=>setForm({...form,minQuantity:e.target.value})}/>
                </div>
                <div>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Prix d'achat (MAD)</label>
                  <input type="number" style={inp} min="0" step="0.01" value={form.costPrice} onChange={e=>setForm({...form,costPrice:e.target.value})}/>
                </div>
                <div>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Prix de vente (MAD)</label>
                  <input type="number" style={inp} min="0" step="0.01" value={form.sellPrice} onChange={e=>setForm({...form,sellPrice:e.target.value})}/>
                </div>
                {form.costPrice > 0 && form.sellPrice > 0 && (
                  <div style={{ gridColumn:'1/-1', background: margin(form)>0?'#E1F5EE':'#fee2e2', borderRadius:8, padding:'8px 12px', fontSize:12, color:margin(form)>0?T:'#dc2626', fontWeight:500 }}>
                    Marge : {margin(form)}% — Bénéfice : {(form.sellPrice - form.costPrice).toFixed(2)} MAD/unité
                  </div>
                )}
                <div>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Date d'expiration</label>
                  <input type="date" style={inp} value={form.expiryDate} onChange={e=>setForm({...form,expiryDate:e.target.value})}/>
                </div>
                <div>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Code-barres</label>
                  <input style={inp} value={form.barcode} onChange={e=>setForm({...form,barcode:e.target.value})} placeholder="6111245681235"/>
                </div>
                <div style={{ gridColumn:'1/-1' }}>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Fournisseur</label>
                  <input style={inp} value={form.supplier} onChange={e=>setForm({...form,supplier:e.target.value})} placeholder="PharmaTech Maroc"/>
                </div>
                <div style={{ gridColumn:'1/-1' }}>
                  <ImageUpload value={form.imageUrl} onChange={v=>setForm({...form,imageUrl:v})}/>
                </div>
                <div style={{ gridColumn:'1/-1' }}>
                  <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Description</label>
                  <textarea style={{ ...inp, minHeight:55, resize:'vertical' }} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Description optionnelle..."/>
                </div>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button type="button" onClick={()=>setShowModal(false)} style={{ flex:1, background:'#f1f5f9', border:'1px solid #e2e8f0', padding:'10px', borderRadius:8, fontSize:13, cursor:'pointer', color:'#64748b' }}>Annuler</button>
                <button type="submit" disabled={saving} style={{ flex:2, background:saving?'#94a3b8':B, color:'#fff', border:'none', padding:'10px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                  {saving?'⏳ Enregistrement...':(editing?'Mettre à jour':'Ajouter au stock')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Movement Modal */}
      {mvModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:50, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}
          onClick={e=>{ if(e.target===e.currentTarget) setMvModal(null); }}>
          <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:400, overflow:'hidden', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding:'1.2rem 1.5rem', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between' }}>
              <div>
                <h2 style={{ fontSize:15, fontWeight:600 }}>Mouvement de stock</h2>
                <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>{mvModal.name} — Stock actuel : <strong style={{ color:stockColor(mvModal) }}>{mvModal.quantity} {mvModal.unit}</strong></div>
              </div>
              <button onClick={()=>setMvModal(null)} style={{ background:'#f1f5f9', border:'none', width:28, height:28, borderRadius:7, cursor:'pointer' }}>×</button>
            </div>
            <form onSubmit={handleMovement} style={{ padding:'1.5rem', display:'flex', flexDirection:'column', gap:12 }}>
              <div>
                <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:6 }}>Type de mouvement</label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                  {[['in','📥 Entrée',T],['out','📤 Sortie','#dc2626'],['adjust','🔄 Ajuster','#7C3AED']].map(([v,l,c])=>(
                    <button type="button" key={v} onClick={()=>setMv({...mv,type:v})} style={{ background:mv.type===v?c:'#f8fafc', color:mv.type===v?'#fff':'#64748b', border:`1px solid ${mv.type===v?c:'#e2e8f0'}`, padding:'8px 4px', borderRadius:8, fontSize:11, fontWeight:600, cursor:'pointer' }}>{l}</button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>{mv.type==='adjust'?'Nouvelle quantité totale':'Quantité'}</label>
                <input type="number" style={inp} min="1" value={mv.quantity} onChange={e=>setMv({...mv,quantity:e.target.value})} required/>
                {mv.type !== 'adjust' && mvModal.quantity > 0 && (
                  <div style={{ fontSize:11, color:'#94a3b8', marginTop:3 }}>
                    Après mouvement : <strong>{mv.type==='in'?mvModal.quantity+Number(mv.quantity):Math.max(0,mvModal.quantity-Number(mv.quantity))} {mvModal.unit}</strong>
                  </div>
                )}
              </div>
              <div>
                <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Motif</label>
                <select style={inp} value={mv.reason} onChange={e=>setMv({...mv,reason:e.target.value})}>
                  <option value="">Choisir un motif...</option>
                  {(mv.type==='in'?['Achat fournisseur','Retour client','Inventaire initial','Don/échantillon']:mv.type==='out'?['Vente client','Produit périmé','Perte/casse','Don']:['Inventaire physique','Correction erreur']).map(r=><option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:11, color:'#64748b', display:'block', marginBottom:4 }}>Note / Référence</label>
                <input style={inp} value={mv.note} onChange={e=>setMv({...mv,note:e.target.value})} placeholder="N° bon de commande, référence..."/>
              </div>
              <button type="submit" disabled={saving} style={{ background:saving?'#94a3b8':mv.type==='in'?T:mv.type==='out'?'#dc2626':'#7C3AED', color:'#fff', border:'none', padding:'11px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                {saving?'⏳ Enregistrement...':'Enregistrer le mouvement'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

